/**
 * Resource Library API Hooks
 * 专用于资源库面板（角色/场景/道具/实拍）
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import {
  resourceLibraryApi,
  type PaginatedResourceLibraryResponse,
  type ResourceInTag,
} from '@/services/api/resource-library';
import { useCallback, useMemo } from 'react';
import {
  projectElementsApi,
  type ElementCoverImageUpdatePayload,
  type ProjectElementCoverType,
} from '@/services/api/project-elements';
import type { Resource } from '@/types';
import { resolveFootageEditActionFromRecord } from '@/types/edit-image';
import { ResourceType } from '@/types/resource';
import { invalidateAllElementDetails, invalidateElementDetail } from './useProjectElementsDetail';
import { invalidateProjectElementsList } from './useProjectElements';
import {
  invalidateAllStoryboardBoardQueries,
  getStoryboardBoardListsData,
  setStoryboardBoardListsData,
} from './useStoryboardBoard';
import { invalidateAllStoryboardQueries } from './useStoryboards';
import { removeStoryboardBoardResourceItems } from '@/lib/storyboard-board-cache';
import { clearStoryboardBoardCache } from '@/lib/storyboard/board-cache';
import { removeDeletedProjectElementFromAllCaches } from '@/lib/realtime/project-element-cache-sync';
import { mergeProjectResourceMediaMetadata } from '@/lib/resources/project-resource-media';
import { toResourceSortParams, type ResourceSortState } from '@/lib/resources/resource-sort';
import { useEditorStore } from '@/stores/editorStore';
import { invalidateMentions } from './useMentionableResources';

export type ProjectElementType = 'character' | 'location' | 'prop';
export type ResourceLibraryType = 'character' | 'location' | 'prop' | 'footage';

export interface PaginatedResourceList {
  items: Resource[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const DEFAULT_RESOURCE_LIBRARY_PAGE_SIZE = 100;
const MAX_RESOURCE_LIBRARY_PAGE_SIZE = 100;

export interface CreateProjectElementPayload {
  name: string;
  description?: string;
  tags?: string[];
}

export function invalidateProjectElementCreationCaches(
  queryClient: QueryClient,
  params: {
    parentProjectId: string;
    contextProjectId?: string | null;
    type: ProjectElementType;
  }
): void {
  const projectIds = Array.from(
    new Set([params.parentProjectId, params.contextProjectId].filter(Boolean) as string[])
  );

  clearStoryboardBoardCache();
  for (const projectId of projectIds) {
    invalidateResourceLibrary(queryClient, projectId, params.type);
    invalidateProjectElementsList(queryClient, projectId, params.type);
    void queryClient.invalidateQueries({
      queryKey: ['project-elements-for-picker', projectId],
    });
  }
  void queryClient.invalidateQueries({ queryKey: ['storyboard-items'] });
  invalidateAllStoryboardQueries(queryClient);
  invalidateAllStoryboardBoardQueries(queryClient);
  invalidateAllElementDetails(queryClient);
  invalidateMentions(queryClient);
}

export interface ResourceLibraryWriteProjectContext {
  /** 写接口权限上下文：团队/企业单集子项目 ID。 */
  permissionProjectId: string | null;
  /** 查询和缓存上下文：编辑页资源库使用当前子项目 ID。 */
  resourceLibraryProjectId: string | null;
}

function resolveWriteProjectContext(
  context: ResourceLibraryWriteProjectContext
): Required<ResourceLibraryWriteProjectContext> {
  return {
    permissionProjectId: context.permissionProjectId,
    resourceLibraryProjectId: context.resourceLibraryProjectId ?? context.permissionProjectId,
  };
}

export const resourceLibraryKeys = {
  all: ['resource-library', 'v1'] as const,
  lists: () => [...resourceLibraryKeys.all, 'list'] as const,
  list: (
    projectId: string,
    type: ResourceLibraryType,
    search?: string,
    filters?: Record<string, string | undefined>,
    sort?: ResourceSortState
  ) =>
    [
      ...resourceLibraryKeys.lists(),
      projectId,
      type,
      search ?? '',
      filters ?? {},
      toResourceSortParams(sort),
    ] as const,
  infinites: () => [...resourceLibraryKeys.all, 'infinite'] as const,
  infinite: (
    projectId: string,
    type: ResourceLibraryType,
    search?: string,
    pageSize = DEFAULT_RESOURCE_LIBRARY_PAGE_SIZE,
    filters?: Record<string, string | undefined>,
    sort?: ResourceSortState
  ) =>
    [
      ...resourceLibraryKeys.infinites(),
      projectId,
      type,
      search ?? '',
      pageSize,
      filters ?? {},
      toResourceSortParams(sort),
    ] as const,
  tagResourcesPrefix: (projectId?: string, tagId?: string) => {
    const prefix = [...resourceLibraryKeys.all, 'tag-resources'] as const;
    if (!projectId) return prefix;
    if (!tagId) return [...prefix, projectId] as const;
    return [...prefix, projectId, tagId] as const;
  },
  tagResources: (projectId: string, tagId: string, page: number, pageSize: number) =>
    [...resourceLibraryKeys.all, 'tag-resources', projectId, tagId, page, pageSize] as const,
};

export function isTagResourcesQueryKey(
  queryKey: readonly unknown[],
  params?: { projectId?: string; tagId?: string }
): boolean {
  if (queryKey.length < 5) return false;
  if (
    queryKey[0] !== resourceLibraryKeys.all[0] ||
    queryKey[1] !== resourceLibraryKeys.all[1] ||
    queryKey[2] !== 'tag-resources'
  ) {
    return false;
  }
  if (params?.projectId && queryKey[3] !== params.projectId) return false;
  if (params?.tagId && queryKey[4] !== params.tagId) return false;
  return true;
}

export function resolveElementResourceThumbnailUrl(imageUrl?: string | null): string | undefined {
  const normalizedImageUrl = typeof imageUrl === 'string' ? imageUrl.trim() : '';
  return normalizedImageUrl ? (imageUrl ?? undefined) : undefined;
}

interface ElementTagMetaResource {
  projectResourceId?: string;
  thumbnailUrl?: string | null;
  fileUrl: string;
}

interface ElementTagMetaRecord {
  tagId: string;
  tagLabel: string;
  displayOrder: number;
  selectedResourceId?: string | null;
  thumbnailUrl?: string | null;
  fileUrl?: string | null;
  resources: ElementTagMetaResource[];
}

function mapElementTagsForMeta(
  tags:
    | Array<{
        tagId: string;
        tagLabel: string;
        selectedResourceId?: string | null;
        tagImageUrl?: string | null;
        resourceUrl?: string | null;
      }>
    | undefined
): ElementTagMetaRecord[] {
  return (tags ?? []).map((tag) => {
    const tagDisplayUrl = tag.tagImageUrl ?? tag.resourceUrl;

    return {
      tagId: tag.tagId,
      tagLabel: tag.tagLabel,
      displayOrder: 0,
      selectedResourceId: tag.selectedResourceId ?? null,
      thumbnailUrl: tagDisplayUrl,
      fileUrl: tagDisplayUrl,
      resources: tagDisplayUrl
        ? [
            {
              projectResourceId: tag.selectedResourceId ?? undefined,
              thumbnailUrl: tagDisplayUrl,
              fileUrl: tagDisplayUrl,
            },
          ]
        : [],
    };
  });
}

function buildElementResource(params: {
  id: string;
  projectId: string;
  name: string;
  type: ResourceType;
  description?: string | null;
  imageUrl?: string | null;
  tagResources: Array<{ thumbnailUrl?: string | null; fileUrl: string }>;
  tags?: ElementTagMetaRecord[];
  defaultTagId?: string | null;
  isOffscreen?: boolean;
  linkedScreenplays?: Resource['linkedScreenplays'];
  canManageAsset?: boolean;
  createdAt: string;
  updatedAt: string;
}) {
  const resourceCount = params.tagResources.length;
  const thumbnailUrl = resolveElementResourceThumbnailUrl(params.imageUrl);

  const res: Resource = {
    id: params.id,
    projectId: params.projectId,
    name: params.name,
    type: params.type,
    fileUrl: thumbnailUrl ?? '',
    thumbnailUrl,
    fileSize: 0,
    mimeType: 'image/jpeg',
    width:
      params.type === ResourceType.CHARACTER ? 400 : params.type === ResourceType.SCENE ? 800 : 400,
    height:
      params.type === ResourceType.CHARACTER ? 533 : params.type === ResourceType.SCENE ? 450 : 400,
    metadata: {
      resourceCount,
      description: params.description ?? '',
      tags: params.tags || [],
      defaultTagId: params.defaultTagId || undefined,
    },
    linkedScreenplays: params.linkedScreenplays ?? [],
    canManageAsset: params.canManageAsset,
    isOffscreen: params.isOffscreen ?? false,
    createdBy: 'system',
    isPublic: false,
    createdAt: params.createdAt,
    updatedAt: params.updatedAt,
  };

  return res;
}

function clampResourceLibraryPageSize(pageSize?: number): number {
  const normalized = pageSize ?? DEFAULT_RESOURCE_LIBRARY_PAGE_SIZE;
  return Math.max(1, Math.min(normalized, MAX_RESOURCE_LIBRARY_PAGE_SIZE));
}

function mergeResourcesById(current: Resource[] | undefined, incoming: Resource[]): Resource[] {
  if (!current?.length) return incoming;
  if (incoming.length === 0) return current;

  const merged = new Map<string, Resource>();
  current.forEach((item) => merged.set(item.id, item));
  incoming.forEach((item) => merged.set(item.id, item));
  return Array.from(merged.values());
}

export async function prefetchAllResourceLibraryResources(
  queryClient: QueryClient,
  params: {
    projectId: string;
    type: ResourceLibraryType;
    search?: string;
    filterParams?: Record<string, string | undefined>;
    sort?: ResourceSortState;
  }
): Promise<Resource[]> {
  const { projectId, type, search, filterParams, sort } = params;
  const listKey = resourceLibraryKeys.list(projectId, type, search, filterParams, sort);
  const allPagesKey = [
    ...resourceLibraryKeys.all,
    'all-pages',
    projectId,
    type,
    search ?? '',
    filterParams ?? {},
    toResourceSortParams(sort),
  ] as const;

  return queryClient.fetchQuery({
    queryKey: allPagesKey,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      let page = 1;
      let totalPages = 1;
      let mergedResources: Resource[] = [];

      do {
        const result = await fetchResourceLibraryPage(
          projectId,
          type,
          search,
          page,
          DEFAULT_RESOURCE_LIBRARY_PAGE_SIZE,
          filterParams,
          sort
        );
        mergedResources = mergeResourcesById(mergedResources, result.items);
        queryClient.setQueryData<Resource[]>(listKey, (current) =>
          filterParams?.onlyLinkedToCurrentEpisode === 'true'
            ? mergedResources
            : mergeResourcesById(current, result.items)
        );
        totalPages = result.totalPages;
        page += 1;
      } while (page <= totalPages);

      return mergedResources;
    },
  });
}

/**
 * 现拉项目整理里全部文件名称（不走 5 分钟缓存、不按 mediaKind 过滤）。
 * 本地列表缺名时用这次请求补齐，再做重名判断。
 */
export async function fetchProjectFootageImageNames(projectId: string): Promise<string[]> {
  let page = 1;
  let totalPages = 1;
  const names: string[] = [];

  do {
    const result = await fetchResourceLibraryPage(
      projectId,
      'footage',
      undefined,
      page,
      DEFAULT_RESOURCE_LIBRARY_PAGE_SIZE
    );
    for (const item of result.items) {
      const name = item.name?.trim();
      if (name) names.push(name);
    }
    totalPages = Math.max(1, result.totalPages);
    page += 1;
  } while (page <= totalPages);

  return names;
}

async function fetchResourceLibraryPage(
  projectId: string,
  type: ResourceLibraryType,
  search: string | undefined,
  page: number,
  pageSize: number,
  filters?: Record<string, string | undefined>,
  sort?: ResourceSortState
): Promise<PaginatedResourceList> {
  const normalizedPageSize = clampResourceLibraryPageSize(pageSize);
  const params = {
    search,
    page,
    pageSize: normalizedPageSize,
    includeTags: true,
    ...filters,
    onlyOwnedByCurrentEpisode: filters?.onlyOwnedByCurrentEpisode === 'true' || undefined,
    onlyLinkedToCurrentEpisode: filters?.onlyLinkedToCurrentEpisode === 'true' || undefined,
    ...toResourceSortParams(sort),
  };

  if (type === 'character') {
    const data = await projectElementsApi.getCharacters(projectId, params);
    return {
      items: data.items.map((c) => {
        const tagResources = (c.tags ?? []).flatMap((t) => {
          const tagDisplayUrl = t.tagImageUrl ?? t.resourceUrl;
          return tagDisplayUrl ? [{ thumbnailUrl: tagDisplayUrl, fileUrl: tagDisplayUrl }] : [];
        });
        const tagsForMeta = mapElementTagsForMeta(c.tags);
        return buildElementResource({
          id: c.characterId,
          projectId,
          name: c.name,
          description: c.description,
          imageUrl: c.coverUrl,
          type: ResourceType.CHARACTER,
          tagResources,
          tags: tagsForMeta,
          defaultTagId: c.defaultTagId,
          isOffscreen: c.isOffscreen,
          linkedScreenplays: c.linkedScreenplays,
          canManageAsset: c.canManageAsset,
          createdAt: c.createdAt,
          updatedAt: c.updatedAt,
        });
      }),
      total: data.total,
      page: data.page,
      pageSize: data.pageSize,
      totalPages: data.totalPages,
    };
  }

  if (type === 'location') {
    const data = await projectElementsApi.getLocations(projectId, params);
    return {
      items: data.items.map((l) => {
        const tagResources = (l.tags ?? []).flatMap((t) => {
          const tagDisplayUrl = t.tagImageUrl ?? t.resourceUrl;
          return tagDisplayUrl ? [{ thumbnailUrl: tagDisplayUrl, fileUrl: tagDisplayUrl }] : [];
        });
        const tagsForMeta = mapElementTagsForMeta(l.tags);
        return buildElementResource({
          id: l.locationId,
          projectId,
          name: l.name,
          description: l.description,
          imageUrl: l.coverUrl,
          type: ResourceType.SCENE,
          tagResources,
          tags: tagsForMeta,
          defaultTagId: l.defaultTagId,
          linkedScreenplays: l.linkedScreenplays,
          canManageAsset: l.canManageAsset,
          createdAt: l.createdAt,
          updatedAt: l.updatedAt,
        });
      }),
      total: data.total,
      page: data.page,
      pageSize: data.pageSize,
      totalPages: data.totalPages,
    };
  }

  if (type === 'prop') {
    const data = await projectElementsApi.getProps(projectId, params);
    return {
      items: data.items.map((p) => {
        const tagResources = (p.tags ?? []).flatMap((t) => {
          const tagDisplayUrl = t.tagImageUrl ?? t.resourceUrl;
          return tagDisplayUrl ? [{ thumbnailUrl: tagDisplayUrl, fileUrl: tagDisplayUrl }] : [];
        });
        const tagsForMeta = mapElementTagsForMeta(p.tags);
        return buildElementResource({
          id: p.propId,
          projectId,
          name: p.name,
          description: p.description,
          imageUrl: p.coverUrl,
          type: ResourceType.PROP,
          tagResources,
          tags: tagsForMeta,
          defaultTagId: p.defaultTagId,
          linkedScreenplays: p.linkedScreenplays,
          canManageAsset: p.canManageAsset,
          createdAt: p.createdAt,
          updatedAt: p.updatedAt,
        });
      }),
      total: data.total,
      page: data.page,
      pageSize: data.pageSize,
      totalPages: data.totalPages,
    };
  }

  const data = await resourceLibraryApi.getFootageResources(projectId, {
    search,
    page,
    pageSize: normalizedPageSize,
    mediaKind: filters?.mediaKind,
    mediaKinds: filters?.mediaKinds,
    onlyOwnedByCurrentEpisode: params.onlyOwnedByCurrentEpisode,
    onlyLinkedToCurrentEpisode: params.onlyLinkedToCurrentEpisode,
    ...toResourceSortParams(sort),
  });
  return {
    items: data.items.map((f) => {
      const mediaFields = mergeProjectResourceMediaMetadata(f);
      const res: Resource = {
        id: f.projectResourceId,
        projectId,
        name: f.name,
        type: ResourceType.FOOTAGE,
        description: f.description ?? undefined,
        fileUrl: f.fileUrl,
        thumbnailUrl: f.thumbnailUrl ?? undefined,
        fileSize: f.fileSize ?? 0,
        mimeType: f.mimeType ?? '',
        width: f.width ?? undefined,
        height: f.height ?? undefined,
        mediaKind: mediaFields.mediaKind,
        originalFileName: mediaFields.originalFileName,
        duration: mediaFields.duration,
        frameRate: mediaFields.frameRate,
        format: mediaFields.format,
        metadata: mediaFields.metadata,
        humanAssetType: f.humanAssetType ?? null,
        humanAssetStatus: f.humanAssetStatus ?? null,
        canManageAsset: f.canManageAsset,
        createdBy: 'system',
        isPublic: false,
        createdAt: f.createdAt,
        updatedAt: f.updatedAt,
      };
      return res;
    }),
    total: data.total,
    page: data.page,
    pageSize: data.pageSize,
    totalPages: data.totalPages,
  };
}

/**
 * 获取资源库列表（角色/场景/道具/实拍）。
 * 调用方需传入已解析的资源库项目 ID；编辑页统一使用当前子项目 ID。
 */
export function useResourceLibraryResources(
  projectId: string | null,
  filters: {
    type: ResourceLibraryType;
    search?: string;
    filterParams?: Record<string, string | undefined>;
    sort?: ResourceSortState;
  },
  options?: { enabled?: boolean }
) {
  const type = filters.type;
  const search = filters.search;
  const filterParams = filters.filterParams;
  const sort = filters.sort;
  const enabled = options?.enabled ?? true;
  const queryClient = useQueryClient();
  const queryKey = projectId
    ? resourceLibraryKeys.list(projectId, type, search, filterParams, sort)
    : resourceLibraryKeys.lists();

  return useQuery({
    queryKey,
    enabled: !!projectId && enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    queryFn: async (): Promise<Resource[]> => {
      if (!projectId) return [];

      const page = await fetchResourceLibraryPage(
        projectId,
        type,
        search,
        1,
        DEFAULT_RESOURCE_LIBRARY_PAGE_SIZE,
        filterParams,
        sort
      );
      const current = queryClient.getQueryData<Resource[]>(queryKey);
      return filterParams?.onlyLinkedToCurrentEpisode === 'true'
        ? page.items
        : mergeResourcesById(current, page.items);
    },
  });
}

export function useInfiniteResourceLibraryResources(
  projectId: string | null,
  filters: {
    type: ResourceLibraryType;
    search?: string;
    filterParams?: Record<string, string | undefined>;
    sort?: ResourceSortState;
  },
  options?: { enabled?: boolean; pageSize?: number }
) {
  const type = filters.type;
  const search = filters.search;
  const filterParams = filters.filterParams;
  const sort = filters.sort;
  const enabled = options?.enabled ?? true;
  const pageSize = clampResourceLibraryPageSize(options?.pageSize);
  const queryClient = useQueryClient();

  return useInfiniteQuery<PaginatedResourceList>({
    queryKey: projectId
      ? resourceLibraryKeys.infinite(projectId, type, search, pageSize, filterParams, sort)
      : resourceLibraryKeys.infinites(),
    enabled: !!projectId && enabled,
    initialPageParam: 1,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    queryFn: async ({ pageParam }) => {
      if (!projectId) {
        return Promise.resolve({
          items: [],
          total: 0,
          page: 1,
          pageSize,
          totalPages: 0,
        });
      }

      const page = await fetchResourceLibraryPage(
        projectId,
        type,
        search,
        Number(pageParam),
        pageSize,
        filterParams,
        sort
      );
      queryClient.setQueryData<Resource[]>(
        resourceLibraryKeys.list(projectId, type, search, filterParams, sort),
        (current) =>
          filterParams?.onlyLinkedToCurrentEpisode === 'true' && page.page === 1
            ? page.items
            : mergeResourcesById(current, page.items)
      );
      return page;
    },
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    initialData: undefined,
  });
}

/**
 * 获取标签下的资源列表。
 * 调用方需传入已解析的资源库项目 ID。
 */
export function useTagResources(
  projectId: string | null | undefined,
  tagId: string | null | undefined,
  options?: { page?: number; pageSize?: number; enabled?: boolean }
) {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 100;
  const enabled = options?.enabled ?? true;

  return useQuery({
    queryKey:
      projectId && tagId
        ? resourceLibraryKeys.tagResources(projectId, tagId, page, pageSize)
        : [...resourceLibraryKeys.all, 'tag-resources', 'disabled'],
    enabled: Boolean(projectId) && Boolean(tagId) && enabled,
    queryFn: async () => {
      if (!projectId || !tagId) {
        throw new Error('Missing projectId or tagId');
      }
      return resourceLibraryApi.getResourcesByTag(projectId, tagId, {
        page,
        pageSize,
      });
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * 创建项目级元素（角色 / 场景 / 道具），成功后会使资源库列表缓存失效
 */
export function useCreateProjectElement(
  projectId: string | null,
  contextProjectId?: string | null
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      type,
      payload,
    }: {
      type: ProjectElementType;
      payload: CreateProjectElementPayload;
    }) => {
      if (!projectId) throw new Error('请先选择项目');
      const { name, description, tags } = payload;
      const createContextProjectId = contextProjectId || projectId;

      if (type === 'character') {
        const characterPayload = {
          name,
          description: description ?? null,
          roleType: 2,
          isOffscreen: false,
          metaData: {},
          tags: tags?.length ? tags : undefined,
        };
        return projectElementsApi.createCharacter(createContextProjectId, characterPayload);
      }
      if (type === 'location') {
        return projectElementsApi.createLocation(createContextProjectId, {
          name,
          location: null,
          description: description ?? null,
          metaData: {},
          tags: tags?.length ? tags : undefined,
        });
      }
      if (type === 'prop') {
        return projectElementsApi.createProp(createContextProjectId, {
          name,
          description: description ?? null,
          metaData: {},
          tags: tags?.length ? tags : undefined,
        });
      }
      throw new Error(`不支持的资源类型: ${type}`);
    },
    onSuccess: (_, variables) => {
      invalidateProjectElementCreationCaches(queryClient, {
        parentProjectId: projectId!,
        contextProjectId,
        type: variables.type,
      });
    },
  });
}

/**
 * 查询删除项目元素的影响范围。
 * 供组件调用，避免直接 import @/services/api/project-elements。
 */
export function fetchProjectElementDeleteImpact(
  parentProjectId: string,
  type: ProjectElementType,
  elementId: string
) {
  return projectElementsApi.getDeleteImpact(parentProjectId, type, elementId);
}

/**
 * 删除项目级元素（角色 / 场景 / 道具）。
 * 写接口和缓存/查询 key 均使用当前资源库项目 ID。
 */
export function useDeleteProjectElement(context: ResourceLibraryWriteProjectContext) {
  const queryClient = useQueryClient();
  const { permissionProjectId, resourceLibraryProjectId } = resolveWriteProjectContext(context);

  return useMutation({
    mutationFn: async ({
      type,
      elementId,
      confirmCrossScreenplayUsage,
    }: {
      type: ProjectElementType;
      elementId: string;
      confirmCrossScreenplayUsage?: boolean;
    }) => {
      if (!permissionProjectId) throw new Error('请先选择项目');

      if (type === 'character') {
        return projectElementsApi.deleteCharacter(permissionProjectId, elementId, {
          confirmCrossScreenplayUsage,
        });
      }
      if (type === 'location') {
        return projectElementsApi.deleteLocation(permissionProjectId, elementId, {
          confirmCrossScreenplayUsage,
        });
      }
      if (type === 'prop') {
        return projectElementsApi.deleteProp(permissionProjectId, elementId, {
          confirmCrossScreenplayUsage,
        });
      }
      throw new Error(`不支持的资源类型: ${type}`);
    },
    onMutate: async (variables) => {
      if (!resourceLibraryProjectId) return { previousLists: [] };

      await queryClient.cancelQueries({ queryKey: resourceLibraryKeys.all });
      const previousLists = getResourceLibraryListsData(queryClient);
      const previousInfiniteQueries = getResourceLibraryInfiniteQueriesData(queryClient);
      const previousBoardQueries = getStoryboardBoardListsData(queryClient);

      removeDeletedProjectElementFromAllCaches(queryClient, {
        elementId: variables.elementId,
        elementType: variables.type,
        projectId: resourceLibraryProjectId,
      });
      setStoryboardBoardListsData(queryClient, (old) => {
        if (!old) return old;
        return removeStoryboardBoardResourceItems(old, { elementId: variables.elementId });
      });

      const { selectedResourceId, selectResource } = useEditorStore.getState();
      if (selectedResourceId === variables.elementId) {
        selectResource(null, null);
      }

      return { previousLists, previousBoardQueries, previousInfiniteQueries };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      for (const [key, data] of context.previousLists ?? []) {
        queryClient.setQueryData(key, data);
      }
      for (const [key, data] of context.previousInfiniteQueries ?? []) {
        queryClient.setQueryData(key, data);
      }
      for (const [key, data] of context.previousBoardQueries ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSuccess: (_, variables) => {
      if (!resourceLibraryProjectId) return;
      clearStoryboardBoardCache();
      invalidateResourceLibrary(queryClient, resourceLibraryProjectId, variables.type);
      queryClient.invalidateQueries({ queryKey: resourceLibraryKeys.infinites() });
      invalidateResourceLibraryTagResources(queryClient, resourceLibraryProjectId);
      invalidateProjectElementsList(queryClient, resourceLibraryProjectId, variables.type);
      queryClient.invalidateQueries({
        queryKey: ['project-elements-for-picker', resourceLibraryProjectId],
      });
      queryClient.invalidateQueries({ queryKey: ['storyboard-items'] });
      invalidateAllStoryboardQueries(queryClient);
      invalidateAllStoryboardBoardQueries(queryClient);
    },
  });
}

/**
 * 更新元素封面图。
 * 写接口和缓存/查询 key 均使用当前资源库项目 ID。
 */
export function useUpdateElementCoverImage(context: ResourceLibraryWriteProjectContext) {
  const queryClient = useQueryClient();
  const { permissionProjectId, resourceLibraryProjectId } = resolveWriteProjectContext(context);

  return useMutation({
    mutationFn: async ({
      type,
      elementId,
      payload,
    }: {
      type: ProjectElementCoverType;
      elementId: string;
      payload: ElementCoverImageUpdatePayload;
    }) => {
      if (!permissionProjectId) throw new Error('请先选择项目');
      return projectElementsApi.updateElementCoverImage(
        permissionProjectId,
        type,
        elementId,
        payload
      );
    },
    onSuccess: (_, variables) => {
      if (!resourceLibraryProjectId) return;
      clearStoryboardBoardCache();
      invalidateResourceLibrary(queryClient, resourceLibraryProjectId, variables.type);
      queryClient.invalidateQueries({ queryKey: resourceLibraryKeys.infinites() });
      invalidateProjectElementsList(queryClient, resourceLibraryProjectId, variables.type);
      invalidateElementDetail(
        queryClient,
        resourceLibraryProjectId,
        variables.type,
        variables.elementId
      );
      queryClient.invalidateQueries({
        queryKey: ['project-elements-for-picker', resourceLibraryProjectId],
      });
      queryClient.invalidateQueries({ queryKey: ['storyboard-items'] });
      invalidateAllStoryboardQueries(queryClient);
      invalidateAllStoryboardBoardQueries(queryClient);
    },
  });
}

/**
 * 更新项目级元素（角色 / 场景 / 道具），成功后会使资源库列表缓存失效。
 * 写接口和缓存/查询 key 均使用当前资源库项目 ID。
 */
export function useUpdateProjectElement(context: ResourceLibraryWriteProjectContext) {
  const queryClient = useQueryClient();
  const { permissionProjectId, resourceLibraryProjectId } = resolveWriteProjectContext(context);

  return useMutation({
    mutationFn: async ({
      type,
      elementId,
      payload,
    }: {
      type: ProjectElementType;
      elementId: string;
      payload: Partial<CreateProjectElementPayload>;
    }) => {
      if (!permissionProjectId) throw new Error('请先选择项目');

      if (type === 'character') {
        return projectElementsApi.updateCharacter(permissionProjectId, elementId, {
          name: payload.name,
          description: payload.description ?? null,
        });
      }
      if (type === 'location') {
        return projectElementsApi.updateLocation(permissionProjectId, elementId, {
          name: payload.name,
          description: payload.description ?? null,
        });
      }
      if (type === 'prop') {
        return projectElementsApi.updateProp(permissionProjectId, elementId, {
          name: payload.name,
          description: payload.description ?? null,
        });
      }
      throw new Error(`不支持的资源类型: ${type}`);
    },
    onSuccess: (_, variables) => {
      if (!resourceLibraryProjectId) return;
      clearStoryboardBoardCache();
      invalidateResourceLibrary(queryClient, resourceLibraryProjectId, variables.type);
      queryClient.invalidateQueries({ queryKey: resourceLibraryKeys.infinites() });
      invalidateProjectElementsList(queryClient, resourceLibraryProjectId, variables.type);
      queryClient.invalidateQueries({
        queryKey: ['project-elements-for-picker', resourceLibraryProjectId],
      });
      queryClient.invalidateQueries({ queryKey: ['storyboard-items'] });
      invalidateAllStoryboardQueries(queryClient);
      invalidateAllStoryboardBoardQueries(queryClient);
    },
  });
}

/**
 * 失活资源库面板缓存（列表和无限滚动）。
 * 在创建/删除/更新元素或项目素材后调用。
 * projectId 和 type 为可选过滤条件，不传则失活所有资源库缓存。
 */
/**
 * 取消资源库列表正在进行的查询（用于乐观更新前的 cancelQueries）。
 */
export function cancelResourceLibraryListQueries(queryClient: QueryClient): Promise<void> {
  return queryClient.cancelQueries({ queryKey: resourceLibraryKeys.all });
}

/**
 * 读取所有资源库列表缓存数据（用于乐观更新前备份，以便在 onError 中回滚）。
 */
export function getResourceLibraryListsData(
  queryClient: QueryClient
): [QueryKey, Resource[] | undefined][] {
  return queryClient.getQueriesData<Resource[]>({ queryKey: resourceLibraryKeys.lists() });
}

export function getResourceLibraryInfiniteQueriesData(
  queryClient: QueryClient
): [QueryKey, InfiniteData<PaginatedResourceList> | undefined][] {
  return queryClient.getQueriesData<InfiniteData<PaginatedResourceList>>({
    queryKey: resourceLibraryKeys.infinites(),
  });
}

export function useCachedResourceLibraryResources(): Resource[] {
  const queryClient = useQueryClient();

  return useMemo(() => {
    const entries = queryClient.getQueriesData<unknown>({ queryKey: resourceLibraryKeys.all });
    return entries.flatMap(([, data]) => {
      if (Array.isArray(data)) return data as Resource[];
      if (data && typeof data === 'object' && 'items' in data) {
        const items = (data as { items?: unknown }).items;
        return Array.isArray(items) ? (items as Resource[]) : [];
      }
      if (data && typeof data === 'object' && 'pages' in data) {
        const pages = (data as InfiniteData<{ items?: Resource[] }>).pages;
        return pages.flatMap((page) => page.items ?? []);
      }
      return [];
    });
  }, [queryClient]);
}

export function invalidateResourceLibrary(
  queryClient: QueryClient,
  projectId?: string,
  type?: ResourceLibraryType
): void {
  if (!projectId) {
    void queryClient.invalidateQueries({ queryKey: resourceLibraryKeys.all });
    return;
  }
  if (!type) {
    void queryClient.invalidateQueries({
      predicate: (q) => {
        const k = q.queryKey;
        return (
          Array.isArray(k) &&
          k[0] === resourceLibraryKeys.all[0] &&
          k[1] === resourceLibraryKeys.all[1] &&
          k[3] === projectId
        );
      },
    });
    return;
  }
  void queryClient.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      return (
        Array.isArray(k) &&
        k[0] === resourceLibraryKeys.all[0] &&
        k[1] === resourceLibraryKeys.all[1] &&
        (k[2] === 'list' || k[2] === 'infinite' || k[2] === 'all-pages') &&
        k[3] === projectId &&
        k[4] === type
      );
    },
  });
}

export function invalidateResourceLibraryLists(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: resourceLibraryKeys.lists() });
}

/** 分镜关联变动只影响开启“本集已关联分镜”的查询。 */
export function invalidateEpisodeLinkedResourceQueries(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: resourceLibraryKeys.all,
    predicate: ({ queryKey }) => {
      const kind = queryKey[2];
      if (kind !== 'list' && kind !== 'infinite' && kind !== 'all-pages') return false;
      const filters = queryKey[kind === 'infinite' ? 7 : 6];
      return (
        typeof filters === 'object' &&
        filters !== null &&
        'onlyLinkedToCurrentEpisode' in filters &&
        filters.onlyLinkedToCurrentEpisode === 'true'
      );
    },
  });
}

export function useInvalidateResourceLibraryLists(): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => {
    invalidateResourceLibraryLists(queryClient);
  }, [queryClient]);
}

export function invalidateResourceLibraryInfinites(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: resourceLibraryKeys.infinites() });
}

export function invalidateResourceLibraryInfinite(
  queryClient: QueryClient,
  projectId: string,
  type: ResourceLibraryType
): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const k = query.queryKey;
      return (
        Array.isArray(k) &&
        k[0] === resourceLibraryKeys.all[0] &&
        k[1] === resourceLibraryKeys.all[1] &&
        k[2] === 'infinite' &&
        k[3] === projectId &&
        k[4] === type
      );
    },
  });
}

export function removeDeletedResourceLibraryItemFromCache(
  queryClient: QueryClient,
  params: { resourceKind: ResourceLibraryType; resourceId: string }
): void {
  queryClient.setQueriesData<Resource[]>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === resourceLibraryKeys.all[0] &&
          key[1] === resourceLibraryKeys.all[1] &&
          (key[2] === 'list' || key[2] === 'all-pages') &&
          key[4] === params.resourceKind
        );
      },
    },
    (current) => current?.filter((item) => item.id !== params.resourceId)
  );

  queryClient.setQueriesData<InfiniteData<PaginatedResourceList>>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === resourceLibraryKeys.all[0] &&
          key[1] === resourceLibraryKeys.all[1] &&
          key[2] === 'infinite' &&
          key[4] === params.resourceKind
        );
      },
    },
    (current) => {
      if (!current) return current;
      return {
        ...current,
        pages: current.pages.map((page) => {
          const nextItems = page.items.filter((item) => item.id !== params.resourceId);
          const removed = page.items.length - nextItems.length;
          return removed > 0
            ? { ...page, items: nextItems, total: Math.max(0, page.total - removed) }
            : page;
        }),
      };
    }
  );
}

/**
 * 就地更新资源库缓存中某个资源的名称（重命名无感同步）。
 * 覆盖 list / all-pages / infinite 三类缓存；不触发重新请求，避免列表闪烁。
 * 预览面板 footage 列表复用 list 缓存，因此同样会无感更新。
 */
export function updateResourceLibraryItemNameInCache(
  queryClient: QueryClient,
  params: { resourceKind: ResourceLibraryType; resourceId: string; name: string }
): void {
  const patchList = (current: Resource[] | undefined): Resource[] | undefined =>
    current?.map((item) => (item.id === params.resourceId ? { ...item, name: params.name } : item));

  queryClient.setQueriesData<Resource[]>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === resourceLibraryKeys.all[0] &&
          key[1] === resourceLibraryKeys.all[1] &&
          (key[2] === 'list' || key[2] === 'all-pages') &&
          key[4] === params.resourceKind
        );
      },
    },
    patchList
  );

  queryClient.setQueriesData<InfiniteData<PaginatedResourceList>>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === resourceLibraryKeys.all[0] &&
          key[1] === resourceLibraryKeys.all[1] &&
          key[2] === 'infinite' &&
          key[4] === params.resourceKind
        );
      },
    },
    (current) => {
      if (!current) return current;
      return {
        ...current,
        pages: current.pages.map((page) => ({
          ...page,
          items: page.items.map((item) =>
            item.id === params.resourceId ? { ...item, name: params.name } : item
          ),
        })),
      };
    }
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function mapKeepingArrayReference<T>(items: T[], updater: (item: T) => T): T[] {
  let nextItems: T[] | null = null;

  for (let index = 0; index < items.length; index += 1) {
    const current = items[index];
    const next = updater(current);
    if (next === current) continue;

    if (!nextItems) nextItems = items.slice();
    nextItems[index] = next;
  }

  return nextItems ?? items;
}

function updateEmbeddedTagLabel(value: unknown, tagId: string, tagLabel: string): unknown {
  if (!isRecord(value)) return value;

  const rawTagId = value.tagId ?? value.id;
  if (typeof rawTagId !== 'string' || rawTagId.trim() !== tagId) return value;

  const currentTagLabel = typeof value.tagLabel === 'string' ? value.tagLabel.trim() : null;
  return currentTagLabel === tagLabel ? value : { ...value, tagLabel };
}

function updateElementResourceTagLabel(
  resource: Resource,
  params: { elementId: string; tagId: string; tagLabel: string }
): Resource {
  const patch: Record<string, unknown> = {};
  let changed = false;

  if (resource.id === params.elementId) {
    const metadata = resource.metadata;
    if (isRecord(metadata) && Array.isArray(metadata.tags)) {
      const nextTags = mapKeepingArrayReference(metadata.tags, (tag) =>
        updateEmbeddedTagLabel(tag, params.tagId, params.tagLabel)
      );
      if (nextTags !== metadata.tags) {
        patch.metadata = { ...metadata, tags: nextTags };
        changed = true;
      }
    }

    if ('tags' in resource && Array.isArray(resource.tags)) {
      const nextTags = mapKeepingArrayReference(resource.tags, (tag) =>
        updateEmbeddedTagLabel(tag, params.tagId, params.tagLabel)
      );
      if (nextTags !== resource.tags) {
        patch.tags = nextTags;
        changed = true;
      }
    }
  }

  if (resource.elementTagId?.trim() === params.tagId) {
    const currentTagLabel = resource.tagLabel?.trim() ?? null;
    if (currentTagLabel !== params.tagLabel) {
      patch.tagLabel = params.tagLabel;
      changed = true;
    }
  }

  return changed ? { ...resource, ...patch } : resource;
}

function updateRuntimeTagResourceLabel(resource: ResourceInTag, tagLabel: string): ResourceInTag {
  let nextTagLabel: string | undefined;
  let nextSnakeCaseTagLabel: string | undefined;

  if ('tagLabel' in resource) {
    const current = resource.tagLabel;
    if (typeof current !== 'string' || current.trim() !== tagLabel) {
      nextTagLabel = tagLabel;
    }
  }
  if ('tag_label' in resource) {
    const current = resource.tag_label;
    if (typeof current !== 'string' || current.trim() !== tagLabel) {
      nextSnakeCaseTagLabel = tagLabel;
    }
  }

  if (nextTagLabel === undefined && nextSnakeCaseTagLabel === undefined) return resource;
  return {
    ...resource,
    ...(nextTagLabel !== undefined ? { tagLabel: nextTagLabel } : {}),
    ...(nextSnakeCaseTagLabel !== undefined ? { tag_label: nextSnakeCaseTagLabel } : {}),
  };
}

/**
 * 标签更新成功后就地同步资源库缓存中的标签名称，避免等待后台重拉才更新 UI。
 * tag-resources 的元素是 ResourceInTag，仅在运行时确实存在标签名字段时更新。
 */
export function syncProjectElementTagToResourceLibraryCaches(
  queryClient: QueryClient,
  params: {
    elementType: ProjectElementType;
    elementId: string;
    tagId: string;
    tagLabel: string;
  }
): void {
  const tagId = params.tagId.trim();
  const tagLabel = params.tagLabel.trim();
  const updateResource = (resource: Resource) =>
    updateElementResourceTagLabel(resource, {
      elementId: params.elementId,
      tagId,
      tagLabel,
    });

  const updateResourceList = (current: Resource[] | undefined): Resource[] | undefined =>
    current ? mapKeepingArrayReference(current, updateResource) : current;

  queryClient.setQueriesData<Resource[]>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === resourceLibraryKeys.all[0] &&
          key[1] === resourceLibraryKeys.all[1] &&
          (key[2] === 'list' || key[2] === 'all-pages') &&
          key[4] === params.elementType
        );
      },
    },
    updateResourceList
  );

  queryClient.setQueriesData<InfiniteData<PaginatedResourceList>>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === resourceLibraryKeys.all[0] &&
          key[1] === resourceLibraryKeys.all[1] &&
          key[2] === 'infinite' &&
          key[4] === params.elementType
        );
      },
    },
    (current) => {
      if (!current) return current;
      const nextPages = mapKeepingArrayReference(current.pages, (page) => {
        const nextItems = mapKeepingArrayReference(page.items, updateResource);
        return nextItems === page.items ? page : { ...page, items: nextItems };
      });
      return nextPages === current.pages ? current : { ...current, pages: nextPages };
    }
  );

  queryClient.setQueriesData<PaginatedResourceLibraryResponse<ResourceInTag>>(
    {
      predicate: (query) => isTagResourcesQueryKey(query.queryKey, { tagId }),
    },
    (current) => {
      if (!current) return current;
      const nextItems = mapKeepingArrayReference(current.items, (resource) =>
        updateRuntimeTagResourceLabel(resource, tagLabel)
      );
      return nextItems === current.items ? current : { ...current, items: nextItems };
    }
  );
}

/** @deprecated 使用 removeDeletedResourceLibraryItemFromCache */
export function removeDeletedProjectElementResourceFromCache(
  queryClient: QueryClient,
  params: { resourceKind: ProjectElementType; resourceId: string }
): void {
  removeDeletedResourceLibraryItemFromCache(queryClient, params);
}

/** 从资源库缓存收集 footage 编辑溯源（取消再拖入时 layout meta 可能已丢）。 */
export function useFootageEditSourceById(cacheKey?: unknown): Map<string, string> {
  const queryClient = useQueryClient();
  return useMemo(() => {
    void cacheKey;
    return collectFootageEditSourceById(queryClient);
  }, [cacheKey, queryClient]);
}

export function collectFootageEditSourceById(queryClient: QueryClient): Map<string, string> {
  const result = new Map<string, string>();
  const add = (item: Resource | null | undefined) => {
    if (!item) return;
    const id = item.id?.trim();
    const source = item.sourceResourceId?.trim();
    if (!id || !source || id === source) return;
    if (resolveFootageEditActionFromRecord(item) !== 'markup') return;
    result.set(id, source);
  };
  for (const [, data] of queryClient.getQueriesData<Resource[]>({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === resourceLibraryKeys.all[0] &&
        key[1] === resourceLibraryKeys.all[1] &&
        (key[2] === 'list' || key[2] === 'all-pages') &&
        key[4] === 'footage'
      );
    },
  })) {
    for (const item of data ?? []) add(item);
  }
  for (const [, data] of queryClient.getQueriesData<InfiniteData<PaginatedResourceList>>({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === resourceLibraryKeys.all[0] &&
        key[1] === resourceLibraryKeys.all[1] &&
        key[2] === 'infinite' &&
        key[4] === 'footage'
      );
    },
  })) {
    for (const page of data?.pages ?? []) {
      for (const item of page.items ?? []) add(item);
    }
  }
  return result;
}

export function invalidateResourceLibraryTagResources(
  queryClient: QueryClient,
  projectId?: string,
  tagId?: string
): void {
  void queryClient.invalidateQueries({
    queryKey: resourceLibraryKeys.tagResourcesPrefix(projectId, tagId),
  });
}

export function useInvalidateResourceLibraryTagResources() {
  const queryClient = useQueryClient();

  return useCallback(
    (projectId?: string, tagId?: string) => {
      invalidateResourceLibraryTagResources(queryClient, projectId, tagId);
    },
    [queryClient]
  );
}

export function matchesResourceLibraryKeyPrefix(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === resourceLibraryKeys.all[0] && queryKey[1] === resourceLibraryKeys.all[1];
}

export function invalidateResourceLibraryByPredicate(
  queryClient: QueryClient,
  predicate: (queryKey: readonly unknown[]) => boolean
): void {
  void queryClient.invalidateQueries({
    predicate: (query) =>
      matchesResourceLibraryKeyPrefix(query.queryKey) && predicate(query.queryKey),
  });
}

export function fetchResourceLibraryInstantResolve<T>(
  queryClient: QueryClient,
  projectId: string,
  type: string,
  queryFn: () => Promise<T>
): Promise<T> {
  return queryClient.fetchQuery({
    queryKey: [...resourceLibraryKeys.all, 'instant-resolve', projectId, type],
    queryFn,
    staleTime: 1000 * 60,
  });
}
